import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAsync } from '../../lib/useAsync';
import { pagesApi } from '../../api/resources';
import { PageLoader, ErrorState, Field, StatusBadge } from '../../components/ui';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

/**
 * No-code Page Maker / CMS builder.
 *
 * Two modes:
 *  - create: assemble blocks then POST /api/pages (draft).
 *  - edit:   load an existing page (pagesApi.get(slug)), hydrate the builder from
 *            its `components`, and PUT /api/pages/{slug} on save.
 *
 * The existing-pages list shows a status pill plus status-aware actions:
 * Preview (opens /pages/slug in a new tab — works for drafts since admin is
 * authed), Edit, and the approval workflow (Submit / Approve / Publish) reusing
 * the same resource action methods as Stories.
 *
 * Block types map 1:1 to the backend's ALLOWED_BLOCK_TYPES. Note: the file
 * block's wire type is `file-download`. Block payload is sent under `data`
 * (backend accepts `data` or `props`) and persisted as the component `content`.
 */
const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero', icon: '🎬', defaults: { heading: 'Headline', subheading: 'Subtext', cta: 'Learn more', cta_url: '' } },
  { type: 'text', label: 'Text', icon: '📝', defaults: { content: 'Rich text paragraph…' } },
  { type: 'richtext', label: 'Rich Text', icon: '🖋️', defaults: { html: '<p>Formatted content</p>' } },
  { type: 'gallery', label: 'Gallery', icon: '🖼️', defaults: { images: [] } },
  { type: 'video', label: 'Video', icon: '🎥', defaults: { url: '' } },
  { type: 'file-download', label: 'File Download', icon: '📎', defaults: { label: 'Download', url: '' } },
  { type: 'card', label: 'Card Grid', icon: '🃏', defaults: { cards: [] } },
  { type: 'cta', label: 'Call to Action', icon: '📣', defaults: { text: 'Join us', button: 'Get involved', url: '' } },
  { type: 'faq', label: 'FAQ', icon: '❓', defaults: { items: [{ q: 'Question?', a: 'Answer.' }] } },
];

const META = Object.fromEntries(BLOCK_TYPES.map((b) => [b.type, b]));

let blockId = 0;

export default function PageMaker() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { data, loading, error, reload } = useAsync(() => pagesApi.list({ per_page: 50 }), []);

  const [editingSlug, setEditingSlug] = useState(null); // null => create mode
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [actingId, setActingId] = useState(null);

  function addBlock(bt) {
    setBlocks((b) => [...b, { _id: ++blockId, type: bt.type, label: bt.label, icon: bt.icon, data: { ...bt.defaults } }]);
  }
  function removeBlock(id) {
    setBlocks((b) => b.filter((x) => x._id !== id));
  }
  function move(id, dir) {
    setBlocks((b) => {
      const i = b.findIndex((x) => x._id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= b.length) return b;
      const copy = [...b];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  function newPage() {
    setEditingSlug(null);
    setTitle('');
    setBlocks([]);
  }

  /** Load a page into the builder and switch to edit mode. */
  async function editPage(slug) {
    setLoadingPage(true);
    try {
      const page = await pagesApi.get(slug);
      const hydrated = [...(page.components || [])]
        .map((c) => ({ ...c, position: c.position ?? 0 }))
        .sort((a, b) => a.position - b.position)
        .map((c) => {
          const meta = META[c.type] || {};
          return {
            _id: ++blockId,
            type: c.type,
            label: meta.label || c.type,
            icon: meta.icon || '🧩',
            data: c.content ?? c.data ?? c.props ?? {},
          };
        });
      setEditingSlug(page.slug);
      setTitle(page.title || '');
      setBlocks(hydrated);
      toast.info(`Editing “${page.title}”.`);
    } catch (err) {
      toast.error(err.message || 'Could not load page for editing.');
    } finally {
      setLoadingPage(false);
    }
  }

  async function save() {
    setSaving(true);
    const payload = {
      title,
      blocks: blocks.map(({ type, data: d }) => ({ type, data: d })),
    };
    try {
      if (editingSlug) {
        await pagesApi.update(editingSlug, payload);
        toast.success('Page updated.');
      } else {
        const created = await pagesApi.create(payload);
        toast.success('Page created (draft). Submit it for review from the pages list.');
        if (created?.slug) setEditingSlug(created.slug);
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not save page. Confirm /api/pages accepts a blocks payload.');
    } finally {
      setSaving(false);
    }
  }

  /** Run an approval workflow action against a page row. */
  async function runAction(page, action) {
    setActingId(page.id);
    try {
      await pagesApi[action](page.slug);
      if (action === 'publish') {
        toast.success('Published — add it to the navigation via Menu Manager.');
      } else {
        toast.success(`Page ${action === 'submit' ? 'submitted for review' : action + 'd'}.`);
      }
      reload();
    } catch (err) {
      toast.error(err.message || `Could not ${action} page.`);
    } finally {
      setActingId(null);
    }
  }

  function rowActions(p) {
    const status = p.status;
    const busy = actingId === p.id;
    const actions = [];
    if (status === 'draft' || status === 'rejected') {
      actions.push(
        <button key="submit" disabled={busy} onClick={() => runAction(p, 'submit')} className="btn-gold px-3 py-1.5 text-xs">
          Submit
        </button>
      );
    }
    if (isAdmin && status === 'pending') {
      actions.push(
        <button key="approve" disabled={busy} onClick={() => runAction(p, 'approve')} className="btn-gold px-3 py-1.5 text-xs">
          Approve
        </button>,
        <button key="reject" disabled={busy} onClick={() => runAction(p, 'reject')} className="btn-ghost px-3 py-1.5 text-xs text-red-600">
          Reject
        </button>
      );
    }
    if (isAdmin && status === 'approved') {
      actions.push(
        <button key="publish" disabled={busy} onClick={() => runAction(p, 'publish')} className="btn-primary px-3 py-1.5 text-xs">
          Publish
        </button>
      );
    }
    return actions;
  }

  return (
    <div className="space-y-6">
      <Seo title="Page Maker" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Page Maker</h1>
          <p className="text-ink-muted">
            {editingSlug ? `Editing: ${editingSlug}` : 'Build pages visually with reusable content blocks.'}
          </p>
        </div>
        <div className="flex gap-2">
          {editingSlug && (
            <button onClick={newPage} className="btn-ghost" disabled={saving}>
              New page
            </button>
          )}
          <button onClick={save} className="btn-primary" disabled={saving || !title || blocks.length === 0}>
            {saving ? 'Saving…' : editingSlug ? 'Update Page' : 'Save Page'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[240px,1fr]">
        {/* Palette */}
        <div className="card h-fit p-4">
          <p className="eyebrow mb-3">Block Palette</p>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
            {BLOCK_TYPES.map((bt) => (
              <button
                key={bt.type}
                onClick={() => addBlock(bt)}
                className="flex items-center gap-2 rounded-lg border border-cream-300 px-3 py-2 text-sm text-ink-soft transition-colors hover:border-maroon-200 hover:bg-maroon-50"
              >
                <span>{bt.icon}</span>
                {bt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="space-y-4">
          <Field label="Page title">
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="About Our Clubs" />
          </Field>

          {loadingPage ? (
            <div className="card flex h-64 items-center justify-center">
              <PageLoader label="Loading page…" />
            </div>
          ) : blocks.length === 0 ? (
            <div className="card flex h-64 items-center justify-center border-2 border-dashed border-cream-400 bg-surface-alt text-sm text-ink-muted">
              Click a block in the palette to start building.
            </div>
          ) : (
            <div className="space-y-3">
              {blocks.map((b, i) => (
                <div key={b._id} className="card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-semibold text-ink">
                      {b.icon} {b.label}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => move(b._id, -1)} disabled={i === 0} className="btn-ghost px-2 py-1 text-xs">
                        ↑
                      </button>
                      <button onClick={() => move(b._id, 1)} disabled={i === blocks.length - 1} className="btn-ghost px-2 py-1 text-xs">
                        ↓
                      </button>
                      <button onClick={() => removeBlock(b._id)} className="btn-ghost px-2 py-1 text-xs text-red-600">
                        ✕
                      </button>
                    </div>
                  </div>
                  {/* Lightweight per-block string editor (core scope). */}
                  <div className="grid gap-2 sm:grid-cols-2">
                    {Object.entries(b.data)
                      .filter(([, v]) => typeof v === 'string')
                      .map(([key, val]) => (
                        <input
                          key={key}
                          className="input"
                          placeholder={key}
                          value={val}
                          onChange={(e) =>
                            setBlocks((bs) =>
                              bs.map((x) => (x._id === b._id ? { ...x, data: { ...x.data, [key]: e.target.value } } : x))
                            )
                          }
                        />
                      ))}
                    {Object.values(b.data).every((v) => typeof v !== 'string') && (
                      <p className="text-xs text-ink-muted sm:col-span-2">
                        This block stores structured content; edit it via the full editor (coming soon).
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Existing pages */}
      <div className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-ink">Existing Pages</h2>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data?.items?.length ? (
          <ul className="divide-y divide-cream-300 text-sm">
            {data.items.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-ink">{p.title}</span>
                  <StatusBadge status={p.status} />
                  {editingSlug === p.slug && <span className="pill-gold">editing</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/pages/${p.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost px-3 py-1.5 text-xs">
                    {p.status === 'published' ? 'View' : 'Preview'}
                  </Link>
                  <button onClick={() => editPage(p.slug)} className="btn-outline px-3 py-1.5 text-xs">
                    Edit
                  </button>
                  {rowActions(p)}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-muted">No pages yet.</p>
        )}
      </div>
    </div>
  );
}
